/**
 * Overview tab — merged reverse-chronological activity timeline.
 *
 * Merges bookings + payments + messages + audit log into a single feed with
 * per-type icons. Lightweight; relies on the composite payload already loaded.
 */
import type { Customer360Response } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import {
  CalendarCheck2,
  CreditCard,
  type LucideIcon,
  MessageSquare,
  ShieldAlert,
} from 'lucide-react';

interface Props {
  data: Customer360Response;
  currency: string;
}

interface Event {
  iso: string;
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  tone?: 'default' | 'danger' | 'success';
}

function buildEvents(data: Customer360Response, currency: string): Event[] {
  const events: Event[] = [];
  for (const b of data.bookings) {
    const bk = b.booking as {
      id: string;
      status: string;
      type: string;
      createdAt: string;
    };
    events.push({
      iso: bk.createdAt,
      icon: CalendarCheck2,
      title: `${bk.type[0]?.toUpperCase()}${bk.type.slice(1)} booking — ${bk.status.replace('_', ' ')}`,
      subtitle: `#${bk.id.slice(-6)}`,
      tone:
        bk.status === 'cancelled'
          ? 'danger'
          : bk.status === 'checked_out' || bk.status === 'completed'
            ? 'success'
            : 'default',
    });
  }
  for (const p of data.payments) {
    const pay = p as {
      id: string;
      amountMinor: number;
      direction: string;
      kind: string;
      status: string;
      createdAt: string;
    };
    events.push({
      iso: pay.createdAt,
      icon: CreditCard,
      title: `${pay.direction === 'in' ? 'Payment' : 'Refund'} — ${formatPriceMinor(pay.amountMinor, currency)}`,
      subtitle: `${pay.kind} · ${pay.status}`,
      tone: pay.direction === 'out' ? 'danger' : pay.status === 'succeeded' ? 'success' : 'default',
    });
  }
  for (const m of data.messages) {
    const msg = m as { id: string; body: string; createdAt: string; sender?: string };
    events.push({
      iso: msg.createdAt,
      icon: MessageSquare,
      title: msg.sender === 'customer' ? 'Message from customer' : 'Message to customer',
      subtitle: msg.body.slice(0, 80),
    });
  }
  for (const a of data.activityTimeline) {
    const row = a as { id: string; action: string; createdAt: string };
    events.push({
      iso: row.createdAt,
      icon: ShieldAlert,
      title: row.action.replace(/\./g, ' › '),
    });
  }
  return events.sort((x, y) => (y.iso > x.iso ? 1 : -1)).slice(0, 50);
}

export function OverviewTab({ data, currency }: Props) {
  const events = buildEvents(data, currency);
  if (events.length === 0) {
    return <EmptyState label="No activity yet." />;
  }
  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-sm font-semibold">Recent activity</h2>
      </div>
      <ol className="divide-y divide-border">
        {events.map((ev, i) => {
          const Icon = ev.icon;
          const toneClass =
            ev.tone === 'danger'
              ? 'text-rose-600'
              : ev.tone === 'success'
                ? 'text-emerald-600'
                : 'text-muted-foreground';
          return (
            <li key={i} className="flex items-start gap-3 px-5 py-3">
              <span
                className={[
                  'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-secondary',
                  toneClass,
                ].join(' ')}
              >
                <Icon className="size-3.5" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{ev.title}</p>
                {ev.subtitle && (
                  <p className="num mt-0.5 truncate text-xs text-muted-foreground">{ev.subtitle}</p>
                )}
              </div>
              <span className="num shrink-0 text-xs text-muted-foreground">
                {new Date(ev.iso).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-5 py-10 text-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}
